import * as Headless from '@headlessui/react'
import { clsx } from 'clsx'
import React, { forwardRef } from 'react'

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2.5 rounded-lg px-3 py-2 text-sm/6 font-semibold',
    'shadow-sm ring-1',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    // Dark mode
    'dark:focus:ring-offset-gray-900',
  ],
  variant: {
    solid: [
      // Basic layout
      'shadow-sm',
      // Sizing
      'px-3 py-2',
      // Typography
      'text-sm/6 font-semibold',
      // Colors
      'text-white',
      // Background colors
      'bg-gray-900 hover:bg-gray-700',
      'dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100',
      // Focus
      'focus:outline-none focus:ring-2 focus:ring-gray-500/20 focus:ring-offset-2',
      // Disabled
      'disabled:bg-gray-100 disabled:text-gray-400',
      'dark:disabled:bg-gray-800 dark:disabled:text-gray-500',
    ],
    outline: [
      // Basic layout
      'shadow-sm',
      // Sizing
      'px-3 py-2',
      // Typography
      'text-sm/6 font-semibold',
      // Colors
      'text-gray-900 dark:text-white',
      // Border
      'ring-1 ring-gray-300 hover:ring-gray-400',
      'dark:ring-white/10 dark:hover:ring-white/20',
      // Background colors
      'bg-white hover:bg-gray-50',
      'dark:bg-white/5 dark:hover:bg-white/10',
      // Focus
      'focus:outline-none focus:ring-2 focus:ring-gray-500/20 focus:ring-offset-2',
      // Disabled
      'disabled:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-200',
      'dark:disabled:bg-white/5 dark:disabled:text-gray-500 dark:disabled:ring-white/10',
    ],
    ghost: [
      // Basic layout
      'shadow-none',
      // Sizing
      'px-3 py-2',
      // Typography
      'text-sm/6 font-semibold',
      // Colors
      'text-gray-700 hover:text-gray-900',
      'dark:text-gray-300 dark:hover:text-white',
      // Background colors
      'hover:bg-gray-50',
      'dark:hover:bg-white/5',
      // Focus
      'focus:outline-none focus:ring-2 focus:ring-gray-500/20 focus:ring-offset-2',
      // Disabled
      'disabled:text-gray-400',
      'dark:disabled:text-gray-500',
    ],
    link: [
      // Reset layout from base
      'shadow-none px-0 py-0 rounded-none',
      // Typography
      'text-sm/6 font-medium underline decoration-transparent hover:decoration-inherit',
      // Colors
      'text-blue-600 hover:text-blue-800',
      'dark:text-blue-400 dark:hover:text-blue-300',
      // Background
      'bg-transparent',
      // Override rings from base
      'ring-0 focus:ring-0 focus:ring-offset-0',
      // Disabled
      'disabled:text-gray-400 dark:disabled:text-gray-500',
    ],
  },
  size: {
    sm: 'px-2.5 py-1.5 text-xs/5 font-medium',
    md: 'px-3 py-2 text-sm/6 font-semibold',
    lg: 'px-4 py-2.5 text-sm/6 font-semibold',
    xl: 'px-6 py-3 text-base/7 font-semibold',
  },
}

export const Button = forwardRef(function Button(
  {
    className,
    variant = 'solid',
    size = 'md',
    ...props
  }: {
    className?: string
    variant?: 'solid' | 'outline' | 'ghost' | 'link'
    size?: 'sm' | 'md' | 'lg' | 'xl'
  } & React.ComponentPropsWithoutRef<typeof Headless.Button> &
    Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  return (
    <Headless.Button
      {...props}
      className={clsx(
        styles.base,
        styles.variant[variant],
        styles.size[size],
        className
      )}
      ref={ref}
    />
  )
})
